import type { MenuVariant } from "features/menu/blocks/Menu/Menu.types";
import type { MenuItem } from "features/menu/types";
import React, { useState } from "react";
import { Link } from "react-router-dom";

import "./MenuNode.css";

type MenuNodeProps = {
  item: MenuItem;
  variant: MenuVariant;
};

type NodeLinkProps = {
  item: MenuItem;
  className: string;
};

// Единый рендер ссылки пункта меню по href/openIn: new_tab — обычная <a> в новой
// вкладке; same_tab + внутренний путь — SPA-навигация через <Link>; same_tab +
// внешний URL (target_kind=external) — обычная <a> в текущей вкладке.
const NodeLink: React.FC<NodeLinkProps> = ({ item, className }) => {
  if (!item.href) return null;

  if (item.openIn === "new_tab") {
    return (
      <a
        className={className}
        href={item.href}
        target="_blank"
        rel="noreferrer"
      >
        {item.title}
      </a>
    );
  }

  if (item.href.startsWith("/")) {
    return (
      <Link
        className={className}
        to={item.href}
      >
        {item.title}
      </Link>
    );
  }

  return (
    <a
      className={className}
      href={item.href}
    >
      {item.title}
    </a>
  );
};

const MenuNode: React.FC<MenuNodeProps> = ({ item, variant }) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasChildren = item.children.length > 0;
  const hasLink = !!item.href;
  const isInlineIframe = item.openIn === "iframe" && !!item.href;
  const showSubmenu = hasChildren && (variant === "flat" || isOpen);
  const toggleOpen = () => setIsOpen((open) => !open);

  return (
    <li className="menu-node">
      <span className="menu-node__row">
        {isInlineIframe ? (
          <button
            type="button"
            className="menu-node__toggle-label"
            aria-expanded={isOpen}
            onClick={toggleOpen}
          >
            {item.title}
          </button>
        ) : hasLink ? (
          <NodeLink
            item={item}
            className="menu-node__link"
          />
        ) : hasChildren && variant === "dropdown" ? (
          <button
            type="button"
            className="menu-node__toggle-label"
            aria-expanded={isOpen}
            onClick={toggleOpen}
          >
            {item.title}
            <span
              className="menu-node__caret"
              aria-hidden="true"
            >
              ▾
            </span>
          </button>
        ) : (
          <span className="menu-node__label">{item.title}</span>
        )}

        {hasChildren && hasLink && variant === "dropdown" && (
          <button
            type="button"
            className="menu-node__toggle"
            aria-expanded={isOpen}
            aria-label="Показать подпункты"
            onClick={toggleOpen}
          >
            ▾
          </button>
        )}
      </span>

      {isInlineIframe && isOpen && (
        <iframe
          className="menu-node__iframe"
          src={item.href as string}
          title={item.title}
        />
      )}

      {showSubmenu && (
        <ul className="menu__submenu">
          {item.children.map((child) => (
            <li
              key={child.id}
              className="menu-node menu-node--leaf"
            >
              {child.href ? (
                <NodeLink
                  item={child}
                  className="menu-node__link"
                />
              ) : (
                <span className="menu-node__label">{child.title}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default MenuNode;
